// ---------------------------------------------------------------------------
// iconKit/iconRegistry — catalogue partagé d'icônes utilisables comme de
// VRAIS éléments de contenu (pas des icônes d'UI) dans les éditeurs Elakay.
//
// Choix délibéré, voir l'audit : aucune nouvelle librairie n'a été ajoutée.
// `lucide-react` est déjà la norme de facto de tous les éditeurs (déjà une
// dépendance, déjà utilisée par PageBuilder.jsx pour ses propres icônes
// d'outils). `react-icons/fa6` (déjà une dépendance) fournit les logos de
// marques (Facebook, Instagram…) que lucide n'inclut pas volontairement.
//
// Liste volontairement CURATÉE (~150 icônes) plutôt que les ~1500 de lucide
// au complet — import ES nommé => tree-shaking réel (lucide-react déclare
// "sideEffects": false), donc seules les icônes réellement listées ici
// finissent dans le bundle, comme le fait déjà PageBuilder.jsx pour ses
// icônes de barre d'outils (~75 imports nommés).
//
// Chaque entrée : { id, name, label, category, keywords, source, Component,
// emojiFallback }. `id` est stable (namespacé par source, ex. "lucide:heart")
// — c'est la SEULE donnée persistée dans un document ({ content: { iconId } }),
// jamais du HTML/SVG fourni par l'utilisateur (voir renderIconSvgMarkup.js).
// `emojiFallback` sert au rendu email (client email → glyphe unique, même
// convention déjà utilisée par emailBlockRenderer.js pour boutons/produits),
// pas encore consommé dans cette phase (pageBuilder uniquement).
// ---------------------------------------------------------------------------

import {
  // — Général —
  Home, Info, HelpCircle, Settings, Bookmark, Flag, Star, Heart, ThumbsUp, ThumbsDown,
  Smile, Frown, Gift, Award, Trophy, Crown, Sparkles, Zap, Sun, Moon, Cloud,
  // — Actions —
  Check, CheckCircle, X, XCircle, Plus, Minus, PlusCircle, MinusCircle,
  Edit, Edit3, Trash2, Copy, Download, Upload, Send, Share2, RefreshCw, RotateCw,
  Save, Filter, SortAsc, SortDesc, Search, Lock, Unlock, Eye, EyeOff, Bell, BellOff,
  // — Flèches —
  ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowUpRight, ArrowDownRight,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsLeft, ChevronsRight,
  CornerUpLeft, CornerDownRight, CornerDownLeft, Move, MoveHorizontal, MoveVertical,
  // — Commerce —
  ShoppingCart, ShoppingBag, CreditCard, Wallet, DollarSign, Percent, Tag, Tags,
  Receipt, Banknote, Coins, Package, Box, Truck, Store, BadgePercent, HandCoins,
  // — Produit —
  Barcode, QrCode, Scale, Ruler, Shirt, Layers, Boxes,
  // — Communication —
  Mail, Phone, PhoneCall, MessageCircle, MessageSquare, AtSign, Mic, Video, Headphones, Inbox,
  // — Localisation —
  MapPin, Map as MapIcon, Navigation, Compass, Globe, Building, Building2, Milestone,
  // — Médias —
  Image as ImageIcon, Images, Camera, Film, Music, Play, Pause, Volume2, Radio,
  // — Utilisateur —
  User, Users, UserPlus, UserCheck, UserX, Contact, IdCard,
  // — Sécurité —
  Shield, ShieldCheck, ShieldAlert, Key, AlertTriangle, AlertCircle, BadgeCheck,
  // — Paiement —
  Landmark, PiggyBank,
  // — 15 ajoutées (spec: "ajoute 15 icon dans la section icon dans les
  // pages pageBuilder, EmailMarketingEditor, HowWeDoItEditor") — comble de
  // vrais trous du catalogue existant (urgence/temps, badges premium,
  // confiance/support, éco-responsable), aucun doublon avec les ~150 déjà
  // présentes.
  Clock, Calendar, Timer, TrendingUp, TrendingDown, Target, Rocket,
  Flame, Gem, ClipboardCheck, BadgeDollarSign, Megaphone, LifeBuoy, Leaf, Recycle,
} from "lucide-react";

import {
  FaFacebook, FaInstagram, FaYoutube, FaTiktok, FaXTwitter,
  FaLinkedin, FaWhatsapp, FaPinterest, FaSnapchat, FaTelegram,
} from "react-icons/fa6";
import { elakayAutoText } from "../dataElakay";


// Catégories affichées dans le picker — ordre = ordre d'affichage.
export const ICON_CATEGORIES = [
  { id: "general", get label() { return elakayAutoText("h_5376df989fac86", "Général"); } },
  { id: "actions", get label() { return elakayAutoText("h_c3cd636a585b20", "Actions"); } },
  { id: "arrows", get label() { return elakayAutoText("h_adf8432a934948", "Flèches"); } },
  { id: "commerce", get label() { return elakayAutoText("h_d3440a61971278", "Commerce"); } },
  { id: "product", get label() { return elakayAutoText("h_ff673dc28e8991", "Produit"); } },
  { id: "communication", get label() { return elakayAutoText("h_ade0d50c9254cf", "Communication"); } },
  { id: "social", get label() { return elakayAutoText("h_4b975571435db2", "Réseaux sociaux"); } },
  { id: "location", get label() { return elakayAutoText("h_84e3d48379f08b", "Localisation"); } },
  { id: "media", get label() { return elakayAutoText("h_486d82f4995b6d", "Médias"); } },
  { id: "user", get label() { return elakayAutoText("h_0b20cd7f98fde6", "Utilisateur"); } },
  { id: "security", get label() { return elakayAutoText("h_33e96f02748f10", "Sécurité"); } },
  { id: "payment", get label() { return elakayAutoText("h_0564e9bab50acd", "Paiement"); } },
];

const CATEGORY_IDS = new Set(ICON_CATEGORIES.map((c) => c.id));

// id, name, label, category, keywords, source, Component, emojiFallback
const RAW_ICONS = [
  // — Général —
  ["home", Home, "general", ["home", "house", "accueil"], "🏠"],
  ["info", Info, "general", ["info", "information"], "ℹ️"],
  ["help-circle", HelpCircle, "general", ["help", "question", "aide", "faq"], "❓"],
  ["settings", Settings, "general", ["settings", "gear", "réglages", "options"], "⚙️"],
  ["bookmark", Bookmark, "general", ["bookmark", "save", "favori"], "🔖"],
  ["flag", Flag, "general", ["flag", "drapeau", "report"], "🚩"],
  ["star", Star, "general", ["star", "étoile", "favorite", "rating"], "⭐"],
  ["heart", Heart, "general", ["heart", "coeur", "love", "favorite", "like"], "❤️"],
  ["thumbs-up", ThumbsUp, "general", ["thumbs up", "like", "pouce"], "👍"],
  ["thumbs-down", ThumbsDown, "general", ["thumbs down", "dislike", "pouce"], "👎"],
  ["smile", Smile, "general", ["smile", "happy", "sourire"], "🙂"],
  ["frown", Frown, "general", ["frown", "sad", "triste"], "🙁"],
  ["gift", Gift, "general", ["gift", "cadeau", "present"], "🎁"],
  ["award", Award, "general", ["award", "badge", "récompense"], "🏅"],
  ["trophy", Trophy, "general", ["trophy", "winner", "trophée"], "🏆"],
  ["crown", Crown, "general", ["crown", "premium", "vip", "couronne"], "👑"],
  ["sparkles", Sparkles, "general", ["sparkles", "new", "magic", "nouveau"], "✨"],
  ["zap", Zap, "general", ["zap", "fast", "flash", "rapide"], "⚡"],
  ["sun", Sun, "general", ["sun", "soleil", "day"], "☀️"],
  ["moon", Moon, "general", ["moon", "lune", "night"], "🌙"],
  ["cloud", Cloud, "general", ["cloud", "nuage", "weather"], "☁️"],

  // — Actions —
  ["check", Check, "actions", ["check", "ok", "valider", "coche"], "✔️"],
  ["check-circle", CheckCircle, "actions", ["check", "success", "validé", "confirmé"], "✅"],
  ["x", X, "actions", ["x", "close", "fermer", "annuler"], "✖️"],
  ["x-circle", XCircle, "actions", ["x", "error", "erreur", "refusé"], "❌"],
  ["plus", Plus, "actions", ["plus", "add", "ajouter"], "➕"],
  ["minus", Minus, "actions", ["minus", "remove", "retirer"], "➖"],
  ["plus-circle", PlusCircle, "actions", ["plus", "add", "ajouter"], "➕"],
  ["minus-circle", MinusCircle, "actions", ["minus", "remove", "retirer"], "➖"],
  ["edit", Edit, "actions", ["edit", "modifier", "pencil"], "✏️"],
  ["edit-3", Edit3, "actions", ["edit", "modifier", "pencil"], "✏️"],
  ["trash-2", Trash2, "actions", ["trash", "delete", "supprimer"], "🗑️"],
  ["copy", Copy, "actions", ["copy", "duplicate", "copier"], "📋"],
  ["download", Download, "actions", ["download", "télécharger"], "⬇️"],
  ["upload", Upload, "actions", ["upload", "téléverser"], "⬆️"],
  ["send", Send, "actions", ["send", "envoyer", "submit"], "📤"],
  ["share-2", Share2, "actions", ["share", "partager"], "🔗"],
  ["refresh-cw", RefreshCw, "actions", ["refresh", "reload", "actualiser", "sync"], "🔄"],
  ["rotate-cw", RotateCw, "actions", ["rotate", "tourner"], "🔄"],
  ["save", Save, "actions", ["save", "enregistrer"], "💾"],
  ["filter", Filter, "actions", ["filter", "filtre"], "🔎"],
  ["sort-asc", SortAsc, "actions", ["sort", "trier"], "🔼"],
  ["sort-desc", SortDesc, "actions", ["sort", "trier"], "🔽"],
  ["search", Search, "actions", ["search", "recherche", "loupe"], "🔍"],
  ["lock", Lock, "actions", ["lock", "verrou", "secure", "sécurisé"], "🔒"],
  ["unlock", Unlock, "actions", ["unlock", "déverrouiller"], "🔓"],
  ["eye", Eye, "actions", ["eye", "view", "voir", "visible"], "👁️"],
  ["eye-off", EyeOff, "actions", ["eye", "hide", "cacher", "masqué"], "🙈"],
  ["bell", Bell, "actions", ["bell", "notification", "alerte"], "🔔"],
  ["bell-off", BellOff, "actions", ["bell", "mute", "silencieux"], "🔕"],

  // — Flèches —
  ["arrow-left", ArrowLeft, "arrows", ["arrow", "left", "flèche", "gauche"], "⬅️"],
  ["arrow-right", ArrowRight, "arrows", ["arrow", "right", "flèche", "droite"], "➡️"],
  ["arrow-up", ArrowUp, "arrows", ["arrow", "up", "flèche", "haut"], "⬆️"],
  ["arrow-down", ArrowDown, "arrows", ["arrow", "down", "flèche", "bas"], "⬇️"],
  ["arrow-up-right", ArrowUpRight, "arrows", ["arrow", "diagonal", "external"], "↗️"],
  ["arrow-down-right", ArrowDownRight, "arrows", ["arrow", "diagonal"], "↘️"],
  ["chevron-left", ChevronLeft, "arrows", ["chevron", "left", "back", "précédent"], "◀️"],
  ["chevron-right", ChevronRight, "arrows", ["chevron", "right", "next", "suivant"], "▶️"],
  ["chevron-up", ChevronUp, "arrows", ["chevron", "up", "collapse"], "🔼"],
  ["chevron-down", ChevronDown, "arrows", ["chevron", "down", "expand"], "🔽"],
  ["chevrons-left", ChevronsLeft, "arrows", ["chevrons", "first", "début"], "⏮️"],
  ["chevrons-right", ChevronsRight, "arrows", ["chevrons", "last", "fin"], "⏭️"],
  ["corner-up-left", CornerUpLeft, "arrows", ["corner", "reply", "répondre", "retour"], "↩️"],
  ["corner-down-right", CornerDownRight, "arrows", ["corner", "forward", "transférer"], "↪️"],
  ["corner-down-left", CornerDownLeft, "arrows", ["corner", "enter"], "↩️"],
  ["move", Move, "arrows", ["move", "déplacer", "drag"], "✥"],
  ["move-horizontal", MoveHorizontal, "arrows", ["move", "resize", "horizontal"], "↔️"],
  ["move-vertical", MoveVertical, "arrows", ["move", "resize", "vertical"], "↕️"],

  // — Commerce —
  ["shopping-cart", ShoppingCart, "commerce", ["cart", "panier", "shop", "buy"], "🛒"],
  ["shopping-bag", ShoppingBag, "commerce", ["bag", "sac", "shop", "shopping"], "🛍️"],
  ["credit-card", CreditCard, "commerce", ["card", "carte", "payment", "paiement"], "💳"],
  ["wallet", Wallet, "commerce", ["wallet", "portefeuille", "money"], "👛"],
  ["dollar-sign", DollarSign, "commerce", ["dollar", "price", "prix", "money"], "💲"],
  ["percent", Percent, "commerce", ["percent", "discount", "remise", "solde"], "％"],
  ["tag", Tag, "commerce", ["tag", "price", "étiquette", "prix", "label"], "🏷️"],
  ["tags", Tags, "commerce", ["tags", "étiquettes", "categories"], "🏷️"],
  ["receipt", Receipt, "commerce", ["receipt", "reçu", "invoice", "facture"], "🧾"],
  ["banknote", Banknote, "commerce", ["banknote", "money", "argent", "cash"], "💵"],
  ["coins", Coins, "commerce", ["coins", "pièces", "money"], "🪙"],
  ["package", Package, "commerce", ["package", "colis", "box", "shipping"], "📦"],
  ["box", Box, "commerce", ["box", "boîte", "package"], "📦"],
  ["truck", Truck, "commerce", ["truck", "shipping", "livraison", "delivery"], "🚚"],
  ["store", Store, "commerce", ["store", "shop", "boutique"], "🏬"],
  ["badge-percent", BadgePercent, "commerce", ["discount", "sale", "promo", "solde"], "🏷️"],
  ["hand-coins", HandCoins, "commerce", ["cashback", "payout", "reversement"], "🤲"],

  // — Produit —
  ["barcode", Barcode, "product", ["barcode", "code-barres", "sku"], "🏷️"],
  ["qr-code", QrCode, "product", ["qr", "qr-code", "scan"], "▪️"],
  ["scale", Scale, "product", ["scale", "weight", "poids", "balance"], "⚖️"],
  ["ruler", Ruler, "product", ["ruler", "size", "taille", "dimensions"], "📏"],
  ["shirt", Shirt, "product", ["shirt", "clothing", "vêtement", "apparel"], "👕"],
  ["layers", Layers, "product", ["layers", "variants", "options"], "🗂️"],
  ["boxes", Boxes, "product", ["boxes", "inventory", "stock"], "📦"],

  // — Communication —
  ["mail", Mail, "communication", ["mail", "email", "courriel"], "✉️"],
  ["phone", Phone, "communication", ["phone", "téléphone", "call"], "📞"],
  ["phone-call", PhoneCall, "communication", ["phone", "call", "appel"], "📱"],
  ["message-circle", MessageCircle, "communication", ["chat", "message", "discussion"], "💬"],
  ["message-square", MessageSquare, "communication", ["chat", "message", "sms"], "💬"],
  ["at-sign", AtSign, "communication", ["at", "email", "mention", "arobase"], "@"],
  ["mic", Mic, "communication", ["mic", "microphone", "audio"], "🎤"],
  ["video", Video, "communication", ["video", "call", "visio"], "🎥"],
  ["headphones", Headphones, "communication", ["headphones", "audio", "support"], "🎧"],
  ["inbox", Inbox, "communication", ["inbox", "messages", "boîte de réception"], "📥"],

  // — Réseaux sociaux (react-icons/fa6, lucide n'inclut pas les logos de marque) —
  ["facebook", FaFacebook, "social", ["facebook", "meta", "social"], "📘"],
  ["instagram", FaInstagram, "social", ["instagram", "insta", "social"], "📷"],
  ["youtube", FaYoutube, "social", ["youtube", "video", "social"], "▶️"],
  ["tiktok", FaTiktok, "social", ["tiktok", "social", "video"], "🎵"],
  ["x-twitter", FaXTwitter, "social", ["twitter", "x", "social"], "𝕏"],
  ["linkedin", FaLinkedin, "social", ["linkedin", "professionnel", "social"], "💼"],
  ["whatsapp", FaWhatsapp, "social", ["whatsapp", "message", "chat"], "💬"],
  ["pinterest", FaPinterest, "social", ["pinterest", "social"], "📌"],
  ["snapchat", FaSnapchat, "social", ["snapchat", "social"], "👻"],
  ["telegram", FaTelegram, "social", ["telegram", "message", "chat"], "✈️"],

  // — Localisation —
  ["map-pin", MapPin, "location", ["location", "pin", "adresse", "map"], "📍"],
  ["map", MapIcon, "location", ["map", "carte", "plan"], "🗺️"],
  ["navigation", Navigation, "location", ["navigation", "gps", "directions"], "🧭"],
  ["compass", Compass, "location", ["compass", "boussole", "direction"], "🧭"],
  ["globe", Globe, "location", ["globe", "world", "monde", "international"], "🌍"],
  ["building", Building, "location", ["building", "bâtiment", "office"], "🏢"],
  ["building-2", Building2, "location", ["building", "company", "entreprise"], "🏢"],
  ["milestone", Milestone, "location", ["milestone", "étape", "distance"], "🚧"],

  // — Médias —
  ["image", ImageIcon, "media", ["image", "photo", "picture"], "🖼️"],
  ["images", Images, "media", ["images", "gallery", "galerie"], "🖼️"],
  ["camera", Camera, "media", ["camera", "photo", "appareil photo"], "📷"],
  ["film", Film, "media", ["film", "video", "movie"], "🎬"],
  ["music", Music, "media", ["music", "musique", "audio"], "🎵"],
  ["play", Play, "media", ["play", "lecture", "start"], "▶️"],
  ["pause", Pause, "media", ["pause"], "⏸️"],
  ["volume-2", Volume2, "media", ["volume", "sound", "son"], "🔊"],
  ["radio", Radio, "media", ["radio", "live", "direct"], "📻"],

  // — Utilisateur —
  ["user", User, "user", ["user", "utilisateur", "compte", "profile"], "👤"],
  ["users", Users, "user", ["users", "team", "équipe", "groupe"], "👥"],
  ["user-plus", UserPlus, "user", ["user", "add", "invite", "inscription"], "➕"],
  ["user-check", UserCheck, "user", ["user", "verified", "vérifié"], "✅"],
  ["user-x", UserX, "user", ["user", "remove", "blocked", "bloqué"], "🚫"],
  ["contact", Contact, "user", ["contact", "carte de visite"], "🪪"],
  ["id-card", IdCard, "user", ["id", "identity", "identité", "carte"], "🪪"],

  // — Sécurité —
  ["shield", Shield, "security", ["shield", "security", "sécurité", "protection"], "🛡️"],
  ["shield-check", ShieldCheck, "security", ["shield", "verified", "sécurisé"], "✅"],
  ["shield-alert", ShieldAlert, "security", ["shield", "warning", "alerte"], "⚠️"],
  ["key", Key, "security", ["key", "clé", "password", "access"], "🔑"],
  ["alert-triangle", AlertTriangle, "security", ["warning", "alert", "attention", "avertissement"], "⚠️"],
  ["alert-circle", AlertCircle, "security", ["alert", "info", "attention"], "❗"],
  ["badge-check", BadgeCheck, "security", ["verified", "vérifié", "trusted"], "✅"],

  // — Paiement —
  ["landmark", Landmark, "payment", ["bank", "banque", "institution"], "🏦"],
  ["piggy-bank", PiggyBank, "payment", ["savings", "épargne", "tirelire"], "🐷"],

  // — 15 ajoutées : urgence/temps (général), badges premium/promo
  // (commerce), confiance/support (communication), éco-responsable
  // (produit) —  aucun doublon, comble de vrais trous du catalogue.
  ["clock", Clock, "general", ["clock", "time", "heure", "urgency", "limited time"], "🕐"],
  ["calendar", Calendar, "general", ["calendar", "date", "événement", "schedule"], "📅"],
  ["timer", Timer, "general", ["timer", "countdown", "compte à rebours"], "⏱️"],
  ["trending-up", TrendingUp, "general", ["trending", "growth", "tendance", "hausse"], "📈"],
  ["trending-down", TrendingDown, "general", ["trending", "decline", "tendance", "baisse"], "📉"],
  ["target", Target, "general", ["target", "goal", "objectif", "cible"], "🎯"],
  ["rocket", Rocket, "general", ["rocket", "launch", "lancement", "boost", "fast"], "🚀"],
  ["flame", Flame, "commerce", ["hot", "trending", "populaire", "best seller", "chaud"], "🔥"],
  ["gem", Gem, "commerce", ["premium", "luxury", "vip", "précieux"], "💎"],
  ["clipboard-check", ClipboardCheck, "commerce", ["order", "confirmed", "commande", "confirmée", "checklist"], "📋"],
  ["badge-dollar-sign", BadgeDollarSign, "commerce", ["price", "deal", "prix", "offre"], "💲"],
  ["megaphone", Megaphone, "communication", ["announcement", "promo", "annonce", "marketing"], "📣"],
  ["life-buoy", LifeBuoy, "communication", ["support", "help", "aide", "sav"], "🛟"],
  ["leaf", Leaf, "product", ["eco", "organic", "bio", "naturel", "durable"], "🍃"],
  ["recycle", Recycle, "product", ["recycle", "sustainable", "durable", "écoresponsable"], "♻️"],
];

// Construction du registre final : id namespacé "<source>:<slug>", figé.
export const ICON_REGISTRY = RAW_ICONS.map(([slug, Component, category, keywords, emojiFallback]) => {
  const source = category === "social" ? "react-icons/fa6" : "lucide";
  if (!CATEGORY_IDS.has(category)) {
    throw new Error(`iconRegistry: unknown category "${category}" for icon "${slug}"`);
  }
  const label = slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return {
    id: `${source === "lucide" ? "lucide" : "fa6"}:${slug}`,
    name: label,
    label,
    category,
    keywords,
    source,
    Component,
    emojiFallback,
  };
});

const ICON_BY_ID = new Map(ICON_REGISTRY.map((icon) => [icon.id, icon]));

// Récupère une icône par id stable, ou undefined si inconnue/obsolète —
// jamais d'exception : un id inconnu dans un vieux document doit juste
// retomber sur le fallback (glyphe libre), pas casser le rendu.
export function getIcon(id) {
  return id ? ICON_BY_ID.get(id) : undefined;
}

export function getIconsByCategory(category) {
  if (!category) return ICON_REGISTRY;
  return ICON_REGISTRY.filter((icon) => icon.category === category);
}

// Recherche simple par nom/mot-clé, insensible à la casse/accents basiques.
export function searchIcons(query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return ICON_REGISTRY;
  return ICON_REGISTRY.filter((icon) =>
    icon.name.toLowerCase().includes(q) ||
    icon.id.toLowerCase().includes(q) ||
    icon.keywords.some((k) => k.toLowerCase().includes(q))
  );
}
